import React, { useCallback, useMemo, useState } from "react";
import { useI18n } from "../../../../hooks/i18n-hooks";
import VStack from "../../../../ui_atoms/VStack";
import ScreenCard, { ButtonType } from "../../../../screens/ScreenCard";
import { LinkSmallTextBold, TitleMediumText } from "../../../FormattedText";
import { StackScreenProps } from "@react-navigation/stack";
import { useLPPEditorContext } from "../context";
import { LPPEditorStackParamList } from "../types";
import Gap from "../../../../ui_atoms/Gap";
import Separator from "../../../../ui_atoms/separator";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { LppPlan } from "../../../../__generated__/graphql";
import { BubbleTalk } from "../../../BubbleTalk";
import DocuIllSvg from "../../../../assets/images/docu_ill.svg";
import ListItem from "../../../../ui_atoms/ListItem";
import { useIntercom } from "../../../../hooks/intercom";
import MarkdownView from "../../../MarkdownView";

type LPPPlanSelectScreenProps = StackScreenProps<LPPEditorStackParamList, "LPPPlanSelect">;

const LPPPlanSelectScreen: React.FC<LPPPlanSelectScreenProps> = () => {
    const i18n = useI18n();
    const { selectedPlan, setSelectedPlan, availablePlans, onSave, useLse, cct } =
        useLPPEditorContext();
    const [isLoading, setIsLoading] = useState(false);
    const { presentArticle } = useIntercom();
    const handleSave = useCallback(async () => {
        setIsLoading(true);
        try {
            await onSave?.(selectedPlan);
        } finally {
            setIsLoading(false);
        }
    }, [onSave, selectedPlan]);
    const handleCancel = useCallback(async () => {
        await onSave?.(undefined);
    }, [onSave]);

    const buttons = useMemo<ButtonType[]>(() => {
        return [
            {
                title: i18n.t("save"),
                onPress: handleSave,
                mode: "secondary",
                validating: isLoading,
                // saving without a selection would silently act as a cancel, so we disable it
                isValid: !isLoading && selectedPlan !== undefined,
            },
            {
                title: i18n.t("cancel"),
                onPress: handleCancel,
                mode: "soft",
                isValid: !isLoading,
            },
        ];
    }, [i18n, handleSave, isLoading, handleCancel, selectedPlan]);

    const insets = useSafeAreaInsets();

    const handlePlanSelect = useCallback(
        (plan: LppPlan) => {
            setSelectedPlan(plan);
        },
        [setSelectedPlan],
    );

    const lppTextKey = useMemo(() => {
        if (useLse) {
            return "lpp.editor.description_lse";
        }
        return "lpp.editor.description_no_lse";
    }, [useLse]);

    return (
        <ScreenCard style={{ paddingTop: insets.top }} buttons={buttons}>
            <VStack>
                <VStack h="2/3">
                    <Gap gap={0.5} />
                    <BubbleTalk
                        personImageComponent={<DocuIllSvg width="100%" height="100%" />}
                        personSize={44}
                        position="top-center"
                    >
                        <TitleMediumText>{i18n.t("lpp.editor.title")}</TitleMediumText>
                    </BubbleTalk>
                    <Gap gap={1} />
                    <MarkdownView markdown={i18n.t(lppTextKey, { cctName: cct?.name })} />
                    <LinkSmallTextBold
                        onPress={() => {
                            void presentArticle("LPPSelection");
                        }}
                    >
                        {i18n.t("know_more")}
                    </LinkSmallTextBold>
                </VStack>
                <Separator v={1 / 2} />
                {availablePlans.map((plan) => (
                    <ListItem
                        key={plan.id}
                        title={plan.name || undefined}
                        selectable
                        checked={selectedPlan?.id === plan.id}
                        description={plan.description || ""}
                        onPress={() => {
                            handlePlanSelect(plan);
                        }}
                    />
                ))}
            </VStack>
        </ScreenCard>
    );
};

export default LPPPlanSelectScreen;
